import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ActionIcon } from '../../assets/icons/tables/action-icon.svg';

interface ListElement {
  icon?: JSX.Element;
  content: JSX.Element | string | Element;
  onClickFunc: (rowId?: string) => void;
  className?: string;
}

interface DropDownMenuProperties {
  dropDownList: ListElement[];
  dropDownIcon?: JSX.Element;
  rowId?: string | null;
  lineBetweenElement?: boolean;
}

/**
 * DropDown component
 * @param {any} properties - The properties
 */
function DropDownMenu(properties: DropDownMenuProperties) {
  const { dropDownList, dropDownIcon, rowId, lineBetweenElement } = properties;
  const reference = useRef<HTMLDivElement>(null);
  const triggerReference = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const { i18n } = useTranslation();
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      reference.current &&
      !reference.current.contains(event.target as Node) &&
      triggerReference.current &&
      !triggerReference.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  /**
   * Open the dropdown and calculate its position
   * @param {any} event The mouse event
   */
  const openDropdown = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (triggerReference.current) {
      const isRTL = i18n.dir() === 'rtl';
      const rect = triggerReference.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 5,
        left: isRTL ? rect.left : rect.right - 100,
      });
    }
    setOpen((previous) => !previous);
  };

  return (
    <div className="drop-down-list-main">
      <div
        className="drop-down-title"
        onClick={openDropdown}
        role="presentation"
        ref={triggerReference}>
        {dropDownIcon !== null ? (
          <div className="drop-down-icon">{dropDownIcon}</div>
        ) : (
          <div className="drop-down-default-icon">
            <ActionIcon />
          </div>
        )}
      </div>
      {open &&
        ReactDOM.createPortal(
          <div className="drop-down-list-main">
            <div
              className={`drop-down-content ${open && 'open'}`}
              ref={reference}
              style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                right: 'auto',
                zIndex: 9999,
              }}>
              {dropDownList.map((value, index) => (
                <div
                  key={index}
                  className={`drop-down-content-list-item ${lineBetweenElement && 'bottom-line'} ${value?.className && value.className}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (rowId) {
                      value.onClickFunc(rowId);
                    } else {
                      value.onClickFunc();
                    }
                    setOpen(false);
                  }}
                  role="presentation">
                  {value.icon && <span>{value.icon}</span>}
                  <p>{value.content as ReactNode} </p>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

DropDownMenu.defaultProps = {
  dropDownIcon: null,
  rowId: null,
  lineBetweenElement: false,
};

export default DropDownMenu;
